// Contact sheet review of symbols (_notes/spec_contact_sheet_review.md): every
// symbol as a tile cropped from the drawing, grouped by class, so the bulk of a
// class can be confirmed in one go and the odd ones out fixed. Confirms the
// CLASS only (typeConfirmed) — text labels and connectivity are reviewed in the
// drawing view.
import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import { Box, Dialog } from '@mui/material';
import EquipmentTypePicker from './EquipmentTypePicker';
import InstrumentPanel from './InstrumentPanel';
import { checkboxSx } from './sheetStyles';
import api from '../utils/api';
import {
  buildSections, reviewProgress, cropWindow, isTypeConfirmed, UNCLASSIFIED, confirmCalls, isInstrument, needsReview, LINE_STYLES,
} from '../utils/contactSheet';

const MONO = "'IBM Plex Mono', monospace";
const TILE_SIZES = { S: 96, M: 136, L: 196 };
const GAP = 10;
const PREFS_KEY = 'pandid.contactSheet.prefs';

function readPrefs() {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; }
}

const btn = {
  display: 'inline-flex', alignItems: 'center', gap: '6px', height: '30px', px: '12px', borderRadius: '7px',
  border: '1px solid rgba(120,150,200,.28)', background: 'transparent', color: '#cdd9ee', fontSize: '12.5px',
  fontFamily: "'Lexend', sans-serif", cursor: 'pointer', whiteSpace: 'nowrap',
  '&:hover:not(:disabled)': { borderColor: 'rgba(45,212,191,.6)', color: '#eaf0fb' },
  '&:disabled': { opacity: 0.4, cursor: 'default' },
};
const primaryBtn = { ...btn, background: '#2dd4bf', borderColor: '#2dd4bf', color: '#04211d', fontWeight: 600,
  '&:hover:not(:disabled)': { opacity: 0.9 } };
const kbd = { fontFamily: MONO, fontSize: '10.5px', opacity: 0.7 };

const Tile = memo(function Tile({ el, image, size, selected, focused, onClick, onDoubleClick }) {
  const canvasRef = useRef(null);
  const b = el.boundingBox || { x: 0, y: 0, width: 0, height: 0 };
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;
    const dpr = window.devicePixelRatio || 1;
    const px = Math.round(size * dpr);
    canvas.width = px; canvas.height = px;
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return;
    const w = cropWindow(b, image.naturalWidth, image.naturalHeight);
    const s = px / w.side;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, px, px);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, w.x, w.y, w.side, w.side, 0, 0, px, px);
    const gap = 3 * dpr;
    ctx.strokeStyle = 'rgba(234,88,12,.85)';
    ctx.lineWidth = 1.5 * dpr;
    ctx.strokeRect((b.x - w.x) * s - gap, (b.y - w.y) * s - gap, b.width * s + 2 * gap, b.height * s + 2 * gap);
  }, [image, size, b.x, b.y, b.width, b.height]); // eslint-disable-line react-hooks/exhaustive-deps
  const confirmed = isTypeConfirmed(el);
  return (
    <Box data-tile-id={el._id} role="button" aria-pressed={selected} aria-label={`${el.name || 'unnamed'} ${el.elementType}`}
      onClick={(e) => onClick(e, el)} onDoubleClick={() => onDoubleClick(el)}
      sx={{ width: size, boxSizing: 'content-box', cursor: 'pointer', userSelect: 'none', borderRadius: '8px', p: '3px',
        // drawn inside the tile's padding: a section's content-visibility clips anything painted outside it
        boxShadow: selected ? 'inset 0 0 0 2px #2dd4bf' : focused ? 'inset 0 0 0 2px rgba(45,212,191,.5)' : 'none',
        background: selected ? 'rgba(45,212,191,.12)' : 'transparent' }}>
      <Box sx={{ position: 'relative' }}>
        <canvas ref={canvasRef} width={size} height={size}
          style={{ width: size, height: size, display: 'block', borderRadius: 6, background: '#fff', opacity: confirmed ? 0.55 : 1 }} />
        {confirmed && (
          <Box sx={{ position: 'absolute', top: 4, right: 4, width: 18, height: 18, borderRadius: '50%', background: '#44d07b',
            color: '#04211d', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</Box>
        )}
        {el.instrument && (
          <Box data-testid="line-style-badge" title={`Line: ${el.instrument.lineStyle?.value || 'unknown'}`}
            sx={{ position: 'absolute', left: 5, bottom: 5, px: '6px', py: '3px', borderRadius: '6px', fontFamily: MONO, fontSize: '22px', lineHeight: 1,
              background: 'rgba(13,26,51,.85)', color: (el.instrument.lineStyle?.value || 'unknown') === 'unknown' ? '#f5a524' : '#9fe7da' }}>
            {(LINE_STYLES.find(l => l.value === (el.instrument.lineStyle?.value || 'unknown')) || LINE_STYLES[5]).glyph}
          </Box>
        )}
        {needsReview(el) && (
          <Box title={(el.instrument.derived.reviewReasons || []).join(', ').replace(/_/g, ' ')}
            sx={{ position: 'absolute', top: 5, left: 5, width: 9, height: 9, borderRadius: '50%', background: '#f5a524' }} />
        )}
      </Box>
      <Box sx={{ fontFamily: MONO, fontSize: '10.5px', color: el.name ? '#9fb2d4' : '#4f6488', mt: '3px', px: '2px',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{el.name || '—'}</Box>
    </Box>
  );
});

const MEANINGS = [
  ['measured_variable', 'Measured variable', 'TIC / T0027 — the letter repeats the first letter'],
  ['equipment_reference', 'Equipment served', 'PI / R002 — R002 is the equipment'],
  ['area_or_system_code', 'Area or system code', 'a plant area, unit or system'],
  ['unknown', 'Leave unknown', 'nothing on the drawing says'],
];
const DISMISS_KEY = 'pandid.contactSheet.conventionsSeen';

// The drawing's bottom-line letters, asked once (plan Step C): while the meaning
// is unknown, show the evidence and let a person choose; choosing reparses the
// drawing's instruments on the server.
function ConventionsPrompt({ drawingId, onChanged }) {
  const [info, setInfo] = useState(null);
  const [hidden, setHidden] = useState(() => { try { return (JSON.parse(localStorage.getItem(DISMISS_KEY)) || []).includes(drawingId); } catch { return false; } });
  useEffect(() => {
    let live = true;
    api.get(`/api/drawings/${drawingId}/instrument-conventions`).then(({ data }) => { if (live) setInfo(data); }).catch(() => {});
    return () => { live = false; };
  }, [drawingId]);
  if (!info || hidden || info.conventions.loopPrefixMeaning !== 'unknown' || info.detection.lettered < 3) return null;
  const hide = () => {
    setHidden(true);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...(JSON.parse(localStorage.getItem(DISMISS_KEY)) || []), drawingId])); } catch { /* private mode */ }
  };
  const choose = async (meaning) => {
    if (meaning === 'unknown') { hide(); return; }
    const { data } = await api.put(`/api/drawings/${drawingId}/instrument-conventions`, { loopPrefixMeaning: meaning });
    setInfo(i => ({ ...i, conventions: data.conventions }));
    if (onChanged) await onChanged();
  };
  const d = info.detection;
  return (
    <Box data-testid="conventions-prompt" sx={{ mb: '12px', px: '12px', py: '9px', borderRadius: '8px', background: 'rgba(245,165,36,.07)', border: '1px solid rgba(245,165,36,.3)', fontSize: '12.5px', color: '#f1dcb4' }}>
      <Box sx={{ mb: '7px' }}>
        <b>Letters on instruments' bottom line:</b> {d.matching} of {d.lettered} match the first letter
        (e.g. <span style={{ fontFamily: MONO }}>{d.samples.slice(0, 3).join(', ')}</span>). What do they mean on this drawing?
      </Box>
      <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {MEANINGS.map(([v, l, hint]) => (
          <Box key={v} component="button" title={hint} onClick={() => choose(v)}
            sx={{ ...btn, height: '28px', ...(v === d.suggestion ? { borderColor: 'rgba(45,212,191,.6)', color: '#2dd4bf' } : {}) }}>{l}</Box>
        ))}
      </Box>
    </Box>
  );
}

const MiniBar = ({ pct }) => (
  <Box sx={{ position: 'relative', width: '90px', height: '5px', borderRadius: '999px', background: 'rgba(120,150,200,.18)', overflow: 'hidden', flexShrink: 0 }}>
    <Box sx={{ position: 'absolute', inset: 0, right: `${(1 - pct) * 100}%`, background: 'linear-gradient(90deg,#2dd4bf,#44d07b)', transition: 'right .4s' }} />
  </Box>
);

/**
 * elements: all the drawing's elements (non-symbols are left out here).
 * onBulkUpdate(ids, set, what) -> Promise: set is { typeConfirmed } or { elementType }.
 * onReject(ids) (admins) / onDelete(ids) (everyone else): "not a symbol".
 * onOpenInDrawing(el); traceHint: { onTrace } before tracing has run, else null.
 */
export default function ContactSheet({ elements, imageBlobUrl, isAdmin, onBulkUpdate, onReject, onDelete, onOpenInDrawing, traceHint,
  drawingId, onSaveInstrument, onRefreshElements, active = true }) {
  const [image, setImage] = useState(null);
  useEffect(() => {
    if (!imageBlobUrl) return undefined;
    let live = true;
    const img = new Image();
    img.onload = () => { if (live) setImage(img); };
    img.src = imageBlobUrl;
    return () => { live = false; };
  }, [imageBlobUrl]);

  const [prefs, setPrefs] = useState(() => ({ size: 'M', showConfirmed: false, needsReview: false, ...readPrefs() }));
  useEffect(() => { try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* private mode */ } }, [prefs]);
  const size = TILE_SIZES[prefs.size] || TILE_SIZES.M;

  const [category, setCategory] = useState('all');
  // Sections the user opened or closed: type -> open. Otherwise a section is open
  // while it has symbols to check and closed once they're all confirmed.
  const [openOverride, setOpenOverride] = useState(() => new Map());
  const [selected, setSelected] = useState(() => new Set());
  const [focusedId, setFocusedId] = useState(null);
  const anchorRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const gridRef = useRef(null);

  const byId = useMemo(() => new Map(elements.map(el => [String(el._id), el])), [elements]);
  const progress = useMemo(() => reviewProgress(elements), [elements]);
  const allSections = useMemo(() => buildSections(elements), [elements]);
  const categories = useMemo(() => [...new Set(allSections.map(s => s.category))].sort(), [allSections]);
  const sections = useMemo(() => allSections.filter(s => category === 'all' || s.category === category), [allSections, category]);

  // What each open section shows: its unconfirmed tiles (and confirmed ones if
  // asked, or all of them in a finished section opened again).
  const shown = useMemo(() => sections.map(s => {
    const open = openOverride.has(s.type) ? openOverride.get(s.type) : !s.done;
    const keep = (el) => (prefs.showConfirmed || s.done || !isTypeConfirmed(el)) && (!prefs.needsReview || needsReview(el));
    return { ...s, open, mainShown: open ? s.main.filter(keep) : [], secondShown: open ? s.secondLook.filter(keep) : [] };
  }), [sections, openOverride, prefs.showConfirmed, prefs.needsReview]);
  const toggleSection = useCallback((s) => setOpenOverride(m => new Map(m).set(s.type, !s.open)), []);
  const setAllOpen = useCallback((open) => setOpenOverride(new Map(allSections.map(s => [s.type, open]))), [allSections]);
  const order = useMemo(() => shown.flatMap(s => [...s.mainShown, ...s.secondShown].map(el => String(el._id))), [shown]);

  // Selection follows the elements: drop ids that left the sheet.
  useEffect(() => {
    setSelected(sel => {
      const next = new Set([...sel].filter(id => order.includes(id)));
      return next.size === sel.size ? sel : next;
    });
  }, [order]);

  const selectedIds = useMemo(() => [...selected], [selected]);
  const targetIds = useCallback(() => (selectedIds.length ? selectedIds : focusedId && order.includes(focusedId) ? [focusedId] : []), [selectedIds, focusedId, order]);

  const handleTileClick = useCallback((e, el) => {
    const id = String(el._id);
    setFocusedId(id);
    if (e.shiftKey && anchorRef.current && order.includes(anchorRef.current)) {
      const [a, b] = [order.indexOf(anchorRef.current), order.indexOf(id)].sort((x, y) => x - y);
      setSelected(new Set(order.slice(a, b + 1)));
      return;
    }
    anchorRef.current = id;
    if (e.metaKey || e.ctrlKey) {
      setSelected(sel => { const next = new Set(sel); if (next.has(id)) next.delete(id); else next.add(id); return next; });
    } else {
      setSelected(sel => (sel.size === 1 && sel.has(id) ? new Set() : new Set([id])));
    }
  }, [order]);

  // After an action the focus moves on to the next tile still to do.
  const moveOnFrom = useCallback((ids) => {
    const gone = new Set(ids);
    const at = Math.max(...ids.map(id => order.indexOf(id)));
    const next = order.slice(at + 1).find(id => !gone.has(id)) || order.find(id => !gone.has(id)) || null;
    setFocusedId(next);
    setSelected(new Set());
    anchorRef.current = next;
  }, [order]);

  // moveOn: an action on the selection / focused tile moves the focus on to the
  // next tile; a section's Confirm all leaves selection and focus alone.
  const run = useCallback(async (ids, fn, moveOn = true) => {
    if (!ids.length || busy) return;
    setBusy(true);
    try { await fn(ids); if (moveOn) moveOnFrom(ids); } finally { setBusy(false); }
  }, [busy, moveOnFrom]);

  const confirmable = useCallback((ids) => ids.filter(id => byId.get(id) && byId.get(id).elementType !== UNCLASSIFIED && !isTypeConfirmed(byId.get(id))), [byId]);
  // A retired class is confirmed by moving it to the class it became.
  const confirm = useCallback((ids, what, moveOn = true) => run(confirmable(ids), async (xs) => {
    for (const call of confirmCalls(xs.map(id => byId.get(id)))) await onBulkUpdate(call.ids, call.set, what);
  }, moveOn), [run, confirmable, onBulkUpdate, byId]);
  const setLineStyle = useCallback((value) => {
    const ids = targetIds().filter(id => byId.get(id) && isInstrument(byId.get(id)));
    run(ids, (xs) => onBulkUpdate(xs, { lineStyle: value }, null), false);
  }, [run, targetIds, byId, onBulkUpdate]);
  const changeClass = useCallback((type) => {
    setPickerOpen(false);
    const ids = targetIds();
    run(ids, (xs) => onBulkUpdate(xs, { elementType: type }, null));
  }, [run, targetIds, onBulkUpdate]);
  const notASymbol = useCallback(() => run(targetIds(), isAdmin ? onReject : onDelete), [run, targetIds, isAdmin, onReject, onDelete]);
  const openFocused = useCallback(() => {
    const ids = targetIds();
    if (ids.length === 1 && byId.get(ids[0])) onOpenInDrawing(byId.get(ids[0]));
  }, [targetIds, byId, onOpenInDrawing]);

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (pickerOpen) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      const key = e.key.toLowerCase();
      if (e.key === 'Escape') { setSelected(new Set()); return; }
      if ((e.metaKey || e.ctrlKey) && key === 'a') {
        e.preventDefault();
        const section = shown.find(s => [...s.mainShown, ...s.secondShown].some(el => String(el._id) === focusedId)) || shown[0];
        if (section) setSelected(new Set([...section.mainShown, ...section.secondShown].map(el => String(el._id))));
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (key === 'c') { e.preventDefault(); confirm(targetIds(), null); }
      else if (key === 't') { e.preventDefault(); if (targetIds().length) setPickerOpen(true); }
      else if (key === 'x') { e.preventDefault(); notASymbol(); }
      else if (e.key === 'Enter') { e.preventDefault(); openFocused(); }
      else if (e.key === ' ' && focusedId) {
        e.preventDefault();
        setSelected(sel => { const next = new Set(sel); if (next.has(focusedId)) next.delete(focusedId); else next.add(focusedId); return next; });
      } else if (e.key.startsWith('Arrow') && order.length) {
        e.preventDefault();
        const cols = Math.max(1, Math.floor(((gridRef.current?.clientWidth || 800) - 32) / (size + 6 + GAP)));
        const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -cols, ArrowDown: cols }[e.key];
        const at = focusedId ? order.indexOf(focusedId) : -1;
        const next = order[Math.min(Math.max(at + step, 0), order.length - 1)];
        setFocusedId(next);
        if (e.shiftKey) setSelected(sel => new Set([...sel, next]));
        document.querySelector(`[data-tile-id="${next}"]`)?.scrollIntoView?.({ block: 'nearest' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, pickerOpen, shown, focusedId, order, size, confirm, targetIds, notASymbol, openFocused]);

  const nTargets = selectedIds.length || (focusedId && order.includes(focusedId) ? 1 : 0);
  const instrumentTargets = targetIds().filter(id => byId.get(id) && isInstrument(byId.get(id)));
  const panelEl = targetIds().length === 1 && instrumentTargets.length === 1 ? byId.get(instrumentTargets[0]) : null;
  const hasInstruments = useMemo(() => elements.some(el => !el.metadata?.rejected && isInstrument(el)), [elements]);
  const sectionsLeft = allSections.filter(s => !s.done).length;
  const tileProps = (el) => ({ el, image, size, selected: selected.has(String(el._id)), focused: focusedId === String(el._id),
    onClick: handleTileClick, onDoubleClick: onOpenInDrawing });

  return (
    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', background: '#10182b' }}>
    <Box ref={gridRef} data-testid="contact-sheet" sx={{ flex: 1, minWidth: 0, overflow: 'auto', background: '#10182b' }}>
      {/* sticky header: progress, controls, actions on the selection */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, background: 'rgba(13,26,51,.97)', borderBottom: '1px solid rgba(120,150,200,.16)', px: '20px', pt: '14px', pb: '12px', backdropFilter: 'blur(4px)' }}>
        {traceHint && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', mb: '12px', px: '12px', py: '8px', borderRadius: '8px', background: 'rgba(45,212,191,.08)', border: '1px solid rgba(45,212,191,.3)', fontSize: '13px', color: '#bfeee6' }}>
            <span>Recommended before tracing connectivity: check the symbol types here — lines are traced between these symbols.</span>
            <Box component="button" onClick={traceHint.onTrace} sx={{ ...btn, borderColor: 'rgba(45,212,191,.6)', color: '#2dd4bf' }}>Trace lines →</Box>
          </Box>
        )}
        {hasInstruments && drawingId && <ConventionsPrompt drawingId={drawingId} onChanged={onRefreshElements} />}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Box sx={{ fontFamily: MONO, fontSize: '11px', letterSpacing: '.14em', textTransform: 'uppercase', color: '#2dd4bf', whiteSpace: 'nowrap' }}>Symbols reviewed</Box>
          <Box sx={{ position: 'relative', flex: 1, height: '12px', borderRadius: '999px', background: 'rgba(120,150,200,.18)', overflow: 'hidden' }}>
            <Box data-testid="review-bar" sx={{ position: 'absolute', inset: 0, right: `${(1 - progress.pct) * 100}%`, background: 'linear-gradient(90deg,#2dd4bf,#44d07b)', borderRadius: '999px', transition: 'right .5s cubic-bezier(.2,.9,.3,1.2)' }} />
          </Box>
          <Box data-testid="review-count" sx={{ fontFamily: MONO, fontSize: '14px', color: '#7fe0a6', whiteSpace: 'nowrap' }}>
            {progress.done}<Box component="span" sx={{ color: '#5f7398' }}>/{progress.total}</Box> · {Math.round(progress.pct * 100)}%
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '10px', flexWrap: 'wrap' }}>
          {nTargets > 0 ? (
            <>
              <Box sx={{ fontSize: '13px', color: '#eaf0fb', minWidth: '84px' }}>{selectedIds.length ? `${nTargets} selected` : '1 focused'}</Box>
              <Box component="button" disabled={busy || !confirmable(targetIds()).length} onClick={() => confirm(targetIds(), null)} sx={primaryBtn}>Confirm type <span style={kbd}>C</span></Box>
              <Box component="button" disabled={busy} onClick={() => setPickerOpen(true)} sx={btn}>Change class <span style={kbd}>T</span></Box>
              <Box component="button" disabled={busy} onClick={notASymbol} sx={btn} title={isAdmin ? 'Reject as a false detection' : 'Delete: not a symbol'}>{isAdmin ? 'Reject' : 'Not a symbol'} <span style={kbd}>X</span></Box>
              {instrumentTargets.length > 0 && (
                <Box component="select" aria-label="Line style" value="" disabled={busy} onChange={(e) => e.target.value && setLineStyle(e.target.value)}
                  sx={{ ...btn, width: 'auto', maxWidth: '190px', flex: 'none', pr: '10px', backgroundColor: '#0b1730' }}>
                  <option value="">Line style ({instrumentTargets.length})…</option>
                  {LINE_STYLES.map(l => <option key={l.value} value={l.value}>{l.glyph} {l.label}</option>)}
                </Box>
              )}
              <Box component="button" disabled={nTargets !== 1} onClick={openFocused} sx={btn}>Open in drawing <span style={kbd}>↵</span></Box>
              <Box component="button" onClick={() => { setSelected(new Set()); setFocusedId(null); }} sx={{ ...btn, border: 'none', color: '#8fa3c6' }}>Clear <span style={kbd}>Esc</span></Box>
            </>
          ) : (
            <Box sx={{ fontSize: '12.5px', color: '#7f93b8' }}>
              {progress.done === progress.total && progress.total > 0
                ? 'All symbol types reviewed ✓'
                : `${sectionsLeft} class${sectionsLeft === 1 ? '' : 'es'} to go — Confirm all on each, then fix the odd ones out. Click, shift-click or ⌘-click to select.`}
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          <Box component="button" onClick={() => setAllOpen(true)} sx={{ ...btn, border: 'none', color: '#8fa3c6', px: '6px' }}>Expand all</Box>
          <Box component="button" onClick={() => setAllOpen(false)} sx={{ ...btn, border: 'none', color: '#8fa3c6', px: '6px' }}>Collapse all</Box>
          <Box component="select" value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category"
            sx={{ height: '30px', color: '#cdd9ee', border: '1px solid rgba(120,150,200,.28)', borderRadius: '7px', pl: '10px', pr: '30px', fontSize: '12.5px', cursor: 'pointer',
              // own caret, clear of the right edge (the native one ignores padding in some browsers)
              appearance: 'none', WebkitAppearance: 'none', backgroundColor: '#0b1730', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 11px center', backgroundSize: '10px 6px',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%238fa3c6' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
              '&:hover': { borderColor: 'rgba(45,212,191,.5)' } }}>
            <option value="all">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </Box>
          <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: '#8fa3c6', cursor: 'pointer' }}>
            <Box component="input" type="checkbox" checked={prefs.showConfirmed} onChange={(e) => setPrefs(p => ({ ...p, showConfirmed: e.target.checked }))}
              sx={checkboxSx()} />
            Show confirmed
          </Box>
          {hasInstruments && (
            <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: '#f5a524', cursor: 'pointer' }}>
              <Box component="input" type="checkbox" aria-label="Needs review only" checked={prefs.needsReview}
                onChange={(e) => setPrefs(p => ({ ...p, needsReview: e.target.checked }))} sx={checkboxSx('#f5a524')} />
              Needs review
            </Box>
          )}
          <Box sx={{ display: 'flex', border: '1px solid rgba(120,150,200,.28)', borderRadius: '7px', overflow: 'hidden' }}>
            {Object.keys(TILE_SIZES).map(k => (
              <Box key={k} component="button" onClick={() => setPrefs(p => ({ ...p, size: k }))} aria-label={`Tile size ${k}`}
                sx={{ height: '28px', width: '30px', border: 'none', background: prefs.size === k ? 'rgba(45,212,191,.2)' : 'transparent', color: prefs.size === k ? '#2dd4bf' : '#8fa3c6', fontFamily: MONO, fontSize: '11px', cursor: 'pointer' }}>{k}</Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: '20px', pb: '40px' }}>
        {progress.total === 0 && (
          <Box sx={{ mt: '40px', textAlign: 'center', color: '#7f93b8', fontSize: '14px' }}>No symbols on this drawing yet.</Box>
        )}
        {shown.map(s => {
          const todo = s.main.filter(el => !isTypeConfirmed(el)).map(el => String(el._id));
          const secondTodo = s.secondLook.filter(el => !isTypeConfirmed(el) && !s.unclassified).map(el => String(el._id));
          return (
            <Box key={s.type} data-section={s.type} sx={{ mt: '10px', contentVisibility: 'auto' }}>
              {/* the header bar opens and closes the section (click, Enter or Space) */}
              <Box data-section-header role="button" tabIndex={0} aria-expanded={s.open}
                aria-label={`${s.unclassified ? 'Unclassified' : s.label}, ${s.confirmed} of ${s.total} confirmed — ${s.open ? 'collapse' : 'expand'}`}
                onClick={() => toggleSection(s)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleSection(s); } }}
                sx={{ display: 'flex', alignItems: 'center', gap: '12px', px: '10px', py: '7px', borderRadius: '8px', cursor: 'pointer', userSelect: 'none',
                  background: s.done ? 'rgba(68,208,123,.06)' : 'transparent', border: '1px solid transparent', transition: 'background .12s, border-color .12s',
                  '&:hover': { background: s.done ? 'rgba(68,208,123,.12)' : 'rgba(120,150,200,.10)', borderColor: 'rgba(120,150,200,.22)' },
                  '&:hover .caret': { color: '#eaf0fb' },
                  '&:focus-visible': { outline: '2px solid rgba(45,212,191,.6)', outlineOffset: '1px' },
                  mb: s.open ? '10px' : 0 }}>
                <Box className="caret" aria-hidden sx={{ width: '16px', textAlign: 'center', fontSize: '12px', color: '#8fa3c6', transition: 'transform .15s, color .12s',
                  transform: s.open ? 'rotate(90deg)' : 'none' }}>▶</Box>
                <Box sx={{ fontSize: '15px', fontWeight: 600, color: s.done ? '#7fe0a6' : '#f4f8ff' }}>
                  {s.done ? '✓ ' : ''}{s.unclassified ? 'Unclassified — needs a class' : s.label}
                </Box>
                <Box sx={{ fontFamily: MONO, fontSize: '11.5px', color: '#7f93b8' }}>{s.done ? `all ${s.total} confirmed` : `${s.confirmed}/${s.total}`}</Box>
                <MiniBar pct={s.total ? s.confirmed / s.total : 0} />
                <Box sx={{ fontFamily: MONO, fontSize: '10.5px', color: '#4f6488' }}>{s.category}</Box>
                <Box sx={{ flex: 1 }} />
                {todo.length > 0 && (
                  <Box component="button" disabled={busy} onClick={(e) => { e.stopPropagation(); confirm(todo, `${todo.length} ${s.label}`, false); }}
                    onKeyDown={(e) => e.stopPropagation()} sx={primaryBtn}>
                    Confirm all {todo.length}
                  </Box>
                )}
              </Box>
              {s.mainShown.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${GAP}px` }}>
                  {s.mainShown.map(el => <Tile key={el._id} {...tileProps(el)} />)}
                </Box>
              )}
              {s.secondShown.length > 0 && (
                <Box sx={{ mt: s.unclassified ? 0 : '14px', pt: s.unclassified ? 0 : '10px', borderTop: s.unclassified ? 'none' : '1px dashed rgba(245,165,36,.35)' }}>
                  {!s.unclassified && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '8px' }}>
                      <Box sx={{ fontSize: '12.5px', color: '#f5a524' }}>Second look — Claude changed its mind on these ({s.secondShown.length})</Box>
                      {secondTodo.length > 0 && (
                        <Box component="button" disabled={busy} onClick={() => confirm(secondTodo, `${secondTodo.length} ${s.label}`, false)} sx={{ ...btn, height: '26px' }}>Confirm these {secondTodo.length}</Box>
                      )}
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${GAP}px` }}>
                    {s.secondShown.map(el => <Tile key={el._id} {...tileProps(el)} />)}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)}
        PaperProps={{ sx: { background: '#0b1730', border: '1px solid rgba(120,150,200,.24)', p: '18px', width: 380 } }}>
        <Box sx={{ fontSize: '13px', color: '#cdd9ee', mb: '12px' }}>Change the class of {targetIds().length} symbol{targetIds().length === 1 ? '' : 's'}</Box>
        <EquipmentTypePicker value={null} onChange={changeClass} />
      </Dialog>
    </Box>
    {panelEl && onSaveInstrument && (
      <InstrumentPanel el={panelEl} drawingId={drawingId} onSave={onSaveInstrument}
        onClose={() => { setSelected(new Set()); setFocusedId(null); }} />
    )}
    </Box>
  );
}
